import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Plus, Trash2, Settings } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CalendarOff } from "lucide-react";

interface TableQRCodesProps {
  restaurantId: string;
  restaurantName: string;
}

interface RestaurantTable {
  id: string;
  tableNumber: string;
  seats: number;
  reservable: boolean;
  qrCodeUrl?: string;
}

export function TableQRCodes({ restaurantId, restaurantName }: TableQRCodesProps) {
  const { toast } = useToast();
  const [deleteTableId, setDeleteTableId] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newTableNumber, setNewTableNumber] = useState("");
  const [newTableSeats, setNewTableSeats] = useState("2");
  const [newTableReservable, setNewTableReservable] = useState(true);
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null);
  const [editingNumber, setEditingNumber] = useState("");
  const [editingSeats, setEditingSeats] = useState("");
  const [editingReservable, setEditingReservable] = useState(true);

  const { data: tables = [], isLoading } = useQuery<RestaurantTable[]>({
    queryKey: ['/api/restaurant', restaurantId, 'tables'],
    enabled: !!restaurantId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (tableId: string) => {
      await apiRequest('DELETE', `/api/restaurant/${restaurantId}/tables/${tableId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/restaurant', restaurantId, 'tables'] });
      toast({
        title: "Table Deleted",
        description: "Table has been removed successfully.",
      });
      setDeleteTableId(null);
    },
  });

  const addMutation = useMutation({
    mutationFn: async (data: { tableNumber: string; seats: number; reservable: boolean }) => {
      return await apiRequest('POST', `/api/restaurant/${restaurantId}/tables`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/restaurant', restaurantId, 'tables'] });
      toast({
        title: "Table Added",
        description: "New table has been created successfully.",
      });
      setShowAddDialog(false);
      setNewTableNumber("");
      setNewTableSeats("2");
      setNewTableReservable(true);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string; tableNumber: string; seats: number; reservable: boolean }) => {
      return await apiRequest('PUT', `/api/restaurant/${restaurantId}/tables/${data.id}`, {
        tableNumber: data.tableNumber,
        seats: data.seats,
        reservable: data.reservable,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/restaurant', restaurantId, 'tables'] });
      toast({
        title: "Table Updated",
        description: "Table information has been updated successfully.",
      });
      setEditingTable(null);
    },
  });

  const getTableUrl = (tableNumber: string) => {
    return `${window.location.origin}/menu/${restaurantId}?table=${tableNumber}`;
  };

  const handleDownloadQR = (tableId: string, tableNumber: string) => {
    const canvas = document.getElementById(`qr-code-${tableId}`) as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${restaurantName}-table-${tableNumber}-qr.png`;
      link.href = url;
      link.click();
      toast({
        title: "QR Code Downloaded",
        description: `Table ${tableNumber} QR code has been saved.`,
      });
    }
  };

  const handleAddTable = () => {
    if (newTableNumber.trim()) {
      addMutation.mutate({
        tableNumber: newTableNumber.trim(),
        seats: parseInt(newTableSeats) || 2,
        reservable: newTableReservable,
      });
    }
  };

  const handleUpdateTable = () => {
    if (editingTable && editingNumber.trim()) {
      updateMutation.mutate({
        id: editingTable.id,
        tableNumber: editingNumber.trim(),
        seats: parseInt(editingSeats) || 2,
        reservable: editingReservable,
      });
    }
  };

  const startEditing = (table: RestaurantTable) => {
    setEditingTable(table);
    setEditingNumber(table.tableNumber);
    setEditingSeats(table.seats?.toString() || "2");
    setEditingReservable(table.reservable !== false);
  };

  if (isLoading) {
    return <Card className="p-6">Loading tables...</Card>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Table QR Codes</h3>
        <Button
          onClick={() => setShowAddDialog(true)}
          size="sm"
          className="gap-2"
          data-testid="button-add-table"
        >
          <Plus className="h-4 w-4" />
          Add Table
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
        {tables.map((table) => (
          <Card key={table.id} className="p-4">
            <div className="flex flex-col items-center space-y-3">
              <div className="flex justify-between w-full items-start">
                <div className="text-left">
                  <h4 className="font-bold">Table {table.tableNumber}</h4>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-muted-foreground">{table.seats} seats</p>
                    {table.reservable === false && (
                      <Badge variant="secondary" className="text-[10px] h-4 px-1 gap-1">
                        <CalendarOff className="h-2.5 w-2.5" />
                        Non-reservable
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground"
                  onClick={() => startEditing(table)}
                  data-testid={`button-edit-table-${table.id}`}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </div>
              <div className="bg-white p-2 rounded-lg">
                <QRCodeCanvas
                  id={`qr-code-${table.id}`}
                  value={getTableUrl(table.tableNumber)}
                  size={150}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center break-all">
                {getTableUrl(table.tableNumber)}
              </p>
              <div className="flex gap-2 w-full">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 gap-1"
                  onClick={() => handleDownloadQR(table.id, table.tableNumber)}
                  data-testid={`button-download-qr-${table.id}`}
                >
                  <Download className="h-3 w-3" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteTableId(table.id)}
                  data-testid={`button-delete-table-${table.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Add Table Dialog */}
      <AlertDialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add New Table</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a number or name for the new table.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tableNumber">Table Number / Name</Label>
              <Input
                id="tableNumber"
                value={newTableNumber}
                onChange={(e) => setNewTableNumber(e.target.value)}
                placeholder="e.g., 4, A1, VIP-1"
                data-testid="input-table-number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tableSeats">Number of Seats</Label>
              <Input
                id="tableSeats"
                type="number"
                min="1"
                max="50"
                value={newTableSeats}
                onChange={(e) => setNewTableSeats(e.target.value)}
                placeholder="How many people can sit here?"
                data-testid="input-table-seats"
              />
            </div>
            <div className="flex items-center justify-between space-x-2 bg-muted/50 p-3 rounded-lg border">
              <div className="space-y-0.5">
                <Label htmlFor="reservable" className="text-sm font-medium">Reservable</Label>
                <div className="text-[11px] text-muted-foreground leading-tight">
                  Allow this table to be assigned to reservations.
                </div>
              </div>
              <Switch
                id="reservable"
                checked={newTableReservable}
                onCheckedChange={setNewTableReservable}
                data-testid="switch-reservable"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-add">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAddTable}
              disabled={!newTableNumber.trim() || addMutation.isPending}
              data-testid="button-confirm-add"
            >
              {addMutation.isPending ? "Adding..." : "Add Table"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Table Dialog */}
      <AlertDialog open={!!editingTable} onOpenChange={() => setEditingTable(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Table {editingTable?.tableNumber}</AlertDialogTitle>
            <AlertDialogDescription>
              Update table number/name and seat count.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editTableNumber">Table Number / Name</Label>
              <Input
                id="editTableNumber"
                value={editingNumber}
                onChange={(e) => setEditingNumber(e.target.value)}
                placeholder="e.g., 4, A1, VIP-1"
                data-testid="input-edit-table-number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editTableSeats">Number of Seats</Label>
              <Input
                id="editTableSeats"
                type="number"
                min="1"
                max="50"
                value={editingSeats}
                onChange={(e) => setEditingSeats(e.target.value)}
                placeholder="How many people can sit here?"
                data-testid="input-edit-table-seats"
              />
            </div>
            <div className="flex items-center justify-between space-x-2 bg-muted/50 p-3 rounded-lg border">
              <div className="space-y-0.5">
                <Label htmlFor="edit-reservable" className="text-sm font-medium">Reservable</Label>
                <div className="text-[11px] text-muted-foreground leading-tight">
                  Allow this table to be assigned to reservations.
                </div>
              </div>
              <Switch
                id="edit-reservable"
                checked={editingReservable}
                onCheckedChange={setEditingReservable}
                data-testid="switch-edit-reservable"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-edit">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUpdateTable}
              disabled={!editingNumber.trim() || updateMutation.isPending}
              data-testid="button-confirm-edit"
            >
              {updateMutation.isPending ? "Updating..." : "Update Table"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTableId} onOpenChange={() => setDeleteTableId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Table</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this table? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTableId && deleteMutation.mutate(deleteTableId)}
              disabled={deleteMutation.isPending}
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
